import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { PlaylistAdd as OpeningIcon } from '@mui/icons-material';
import inventoryMovementService from '../../services/inventoryMovementService';
import type { OpeningStockLocationEligibility } from '../../services/inventoryMovementService';
import { toMinorUnits } from '../../utils/money';
import { formatQty } from '../../utils/quantityFormat';

/**
 * INVENTORY-TRUST.1a — Set Opening Stock dialog.
 *
 * First-time stock setup for an item at a location with no prior activity.
 * Locations with movements are shown disabled with an explanation (backed by
 * the eligibility endpoint, so users never discover ineligibility via an
 * error). Books to venue-owned stock — consigned inventory is set up via a
 * consignment receipt, not opening stock.
 */

interface Props {
  open: boolean;
  onClose: () => void;
  venueId: string;
  itemId: string;
  itemName: string;
  defaultCurrency?: string;
  /** Called after a successful post — parent refreshes balances + toasts. */
  onSuccess: () => void;
}

/** Map backend typed error codes to human sentences. */
const ERROR_SENTENCES: Record<string, string> = {
  OPENING_ALREADY_EXISTS: 'Opening stock has already been recorded for this item at that location.',
  ITEM_HAS_MOVEMENTS_AT_LOCATION: 'This item already has activity at that location — use a manual adjustment to correct stock instead.',
  NO_DEFAULT_OWNERSHIP_ACCOUNT: 'This venue has no default ownership account yet, so opening stock cannot be booked. Contact your administrator.',
  LOCATION_INACTIVE: 'That location is inactive — reactivate it or pick another location.',
  LOCATION_NOT_FOUND: 'That location no longer exists — pick another location.',
  ITEM_INACTIVE: 'This item is inactive — reactivate it before setting opening stock.',
  INVALID_QUANTITY: 'The opening quantity must be a positive number.',
  INVALID_UNIT_COST: 'The unit cost must be zero or a positive number.',
};

const OpeningStockDialog: React.FC<Props> = ({ open, onClose, venueId, itemId, itemName, defaultCurrency, onSuccess }) => {
  const theme = useTheme();

  const [eligibility, setEligibility] = useState<OpeningStockLocationEligibility[] | null>(null);
  const [eligibilityError, setEligibilityError] = useState<string | null>(null);
  const [locationId, setLocationId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Load eligibility fresh on every open; reset the form.
  useEffect(() => {
    if (!open) return;
    setEligibility(null);
    setEligibilityError(null);
    setSubmitError(null);
    setQuantity('');
    setUnitCost('');
    inventoryMovementService.getOpeningStockEligibility(venueId, itemId)
      .then(res => {
        setEligibility(res.locations);
        const firstEligible = res.locations.find(l => l.eligible && l.isDefault)
          ?? res.locations.find(l => l.eligible);
        setLocationId(firstEligible?.locationId ?? '');
      })
      .catch(() => setEligibilityError('Could not load locations. Close and try again.'));
  }, [open, venueId, itemId]);

  const activeLocations = useMemo(
    () => (eligibility ?? []).filter(l => l.active),
    [eligibility],
  );
  const anyEligible = activeLocations.some(l => l.eligible);
  const selected = activeLocations.find(l => l.locationId === locationId);

  const quantityNum = Number(quantity);
  const quantityValid = quantity !== '' && Number.isFinite(quantityNum) && quantityNum > 0;
  const unitCostNum = unitCost === '' ? undefined : Number(unitCost);
  const unitCostValid = unitCost === '' || (Number.isFinite(unitCostNum) && (unitCostNum as number) >= 0);
  const canSubmit = !!selected?.eligible && quantityValid && unitCostValid && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit || !selected) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await inventoryMovementService.postOpeningStock(venueId, itemId, {
        locationId: selected.locationId,
        quantity: quantityNum,
        ...(unitCostNum !== undefined && { unitCost: toMinorUnits(unitCostNum) }), // save: major→minor (valued-at line uses typed major)
      });
      onSuccess();
      onClose();
    } catch (err: any) {
      const code = err?.response?.data?.error?.code as string | undefined;
      setSubmitError(
        (code && ERROR_SENTENCES[code])
        ?? err?.response?.data?.error?.message
        ?? 'Failed to set opening stock. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pb: 1 }}>
        <OpeningIcon sx={{ color: theme.palette.primary.main }} />
        <Box>
          <Typography component="span" variant="h6" fontWeight={700} sx={{ display: 'block', lineHeight: 1.2 }}>
            Set opening stock
          </Typography>
          <Typography variant="caption" color="text.secondary">{itemName}</Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        {/* Loading eligibility */}
        {!eligibility && !eligibilityError && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
        )}

        {/* Eligibility load failed */}
        {eligibilityError && <Alert severity="error">{eligibilityError}</Alert>}

        {/* No locations at all */}
        {eligibility && activeLocations.length === 0 && (
          <Alert severity="info">
            This venue has no active locations yet. Create a location first, then set opening stock.
          </Alert>
        )}

        {/* Every location already has activity */}
        {eligibility && activeLocations.length > 0 && !anyEligible && (
          <Alert severity="info">
            Every location already has activity for this item, so its starting balances are already
            on the ledger. Use <strong>Adjust stock</strong> to correct quantities.
          </Alert>
        )}

        {eligibility && anyEligible && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              Declare the stock already on the shelf before Nota started tracking this item.
              Opening stock books to <strong>venue-owned</strong> stock — consigned inventory is
              set up via a consignment receipt instead.
            </Typography>

            <FormControl size="small" fullWidth>
              <InputLabel>Location</InputLabel>
              <Select value={locationId} label="Location" onChange={e => setLocationId(e.target.value)} disabled={submitting}>
                {activeLocations.map(loc => loc.eligible ? (
                  <MenuItem key={loc.locationId} value={loc.locationId}>
                    {loc.name}{loc.isDefault ? ' (default)' : ''}
                  </MenuItem>
                ) : (
                  <Tooltip
                    key={loc.locationId}
                    title={loc.hasOpening
                      ? 'Opening stock was already recorded at this location.'
                      : 'This location already has movements for this item — use Adjust stock instead.'}
                    placement="right"
                  >
                    {/* span wrapper so the tooltip fires on a disabled item */}
                    <span>
                      <MenuItem disabled value={loc.locationId} sx={{ width: '100%' }}>
                        {loc.name}{loc.isDefault ? ' (default)' : ''}
                      </MenuItem>
                    </span>
                  </Tooltip>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Quantity"
              size="small"
              type="number"
              value={quantity}
              onChange={e => setQuantity(e.target.value)}
              disabled={submitting}
              error={quantity !== '' && !quantityValid}
              helperText={quantity !== '' && !quantityValid ? 'Must be a positive number' : ' '}
              slotProps={{ htmlInput: { min: 0, step: 1 } }}
              autoFocus
            />

            <TextField
              label="Unit cost (optional)"
              size="small"
              type="number"
              value={unitCost}
              onChange={e => setUnitCost(e.target.value)}
              disabled={submitting}
              error={!unitCostValid}
              helperText={!unitCostValid ? 'Must be zero or a positive number' : 'Used for stock valuation'}
              slotProps={{
                htmlInput: { min: 0, step: 0.01 },
                input: defaultCurrency
                  ? { startAdornment: <InputAdornment position="start">{defaultCurrency}</InputAdornment> }
                  : undefined,
              }}
            />

            {/* Confirmation line */}
            {selected?.eligible && quantityValid && (
              <Alert severity="info" icon={false} sx={{ py: 0.5 }}>
                This will set the starting balance at <strong>{selected.name}</strong> to{' '}
                <strong>{formatQty(quantityNum)}</strong>
                {unitCostValid && unitCostNum !== undefined
                  ? <> (valued at {defaultCurrency ? `${defaultCurrency} ` : ''}{(quantityNum * unitCostNum).toFixed(2)})</>
                  : null}.
              </Alert>
            )}

            {submitError && <Alert severity="error">{submitError}</Alert>}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Set opening stock
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OpeningStockDialog;
